import * as React from "react"
import { Slot } from "@radix-ui/react-slot"
import { cva } from "class-variance-authority";

import { cn } from "@/lib/utils"

/**
 * whitespace-nowrap and a fixed h-* (rather than min-h-*) is what a real
 * user's phone hit, task: buttons must not truncate at a large accessibility
 * text size, 2026-09-24. nowrap forces a long label onto one line no matter
 * how wide iOS Dynamic Type makes it, and a fixed height cannot grow past
 * itself for a label that DOES wrap, so together they either ran the text
 * off the edge of a `w-full` button or clipped a second line the box was
 * never tall enough to hold ("Copy phone # and schedule meeting", reported
 * against a real device). Every individual button across the app that
 * passed its own h-9/h-10/h-11/h-12 in className had the identical problem,
 * for the identical reason: that override still won over whatever this
 * component's own size variant said, fixed height either way. Those were
 * all converted to min-h-* in the same pass as this file; an icon-only
 * square button (h-N w-N, no text content to wrap) is the one shape that
 * correctly keeps a fixed, non-growing height, both here (the `icon` size
 * variant, deliberately left alone) and at every one of those call sites.
 */
const buttonVariants = cva(
  "inline-flex items-center justify-center gap-2 rounded-md text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0",
  {
    variants: {
      variant: {
        default:
          "bg-primary text-primary-foreground shadow hover:bg-primary/90",
        destructive:
          "bg-destructive text-destructive-foreground shadow-sm hover:bg-destructive/90",
        outline:
          "border border-input bg-transparent shadow-sm hover:bg-accent hover:text-accent-foreground",
        secondary:
          "bg-secondary text-secondary-foreground shadow-sm hover:bg-secondary/80",
        ghost: "hover:bg-accent hover:text-accent-foreground",
        link: "text-primary underline-offset-4 hover:underline",
      },
      size: {
        default: "min-h-9 px-4 py-2",
        sm: "min-h-8 rounded-md px-3 text-xs",
        lg: "min-h-10 rounded-md px-8",
        icon: "h-9 w-9",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  }
)

const Button = React.forwardRef(({ className, variant, size, asChild = false, ...props }, ref) => {
  const Comp = asChild ? Slot : "button"
  return (
    (<Comp
      className={cn(buttonVariants({ variant, size, className }))}
      ref={ref}
      {...props} />)
  );
})
Button.displayName = "Button"

export { Button, buttonVariants }
